import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../../dataManagement/uiDb/UiDb";
import type {
  CreateProductModalState,
  ListingTypeVariantConfig,
  ProductDetailsModal,
} from "../../interfaces/ProductObject";
import type { VoiceClip } from "../../interfaces/VoiceObject";
import type {
  VoiceProductCreateContext,
  VoiceProductCreateResult,
  VoiceCreationAnswers,
} from "../../interfaces/VoiceProductCreateObject";
import { publicationPrepared } from "../../dataManagement/uiDb/VoiceProductCreateDb";
import {
  analyzeProductCreation,
  publishVoiceProduct,
  answerVoiceCreation,
  getVoiceCreationDraft,
  getSavedVoiceDrafts,
  discardVoiceCreation,
} from "../apiServices/VoiceProductCreateApiService";
import VoiceApiService from "../apiServices/VoiceApiService";
import { VoiceApiError } from "../helpers/VoiceApiResponseHelper";
import ProductConvertor from "../converters/uiToBackend/ProductConvertor";
import genericUtility from "../utility/GenericUtility";
import { VOICE_CREATION_CODES } from "../../constants/VoiceCreationCodes";

export interface VoiceCreationFailure {
  message: string;
  code?: string;
  retryable?: boolean;
  disposition?: "rejected" | "uncertain" | "conflict";
  cancelled?: boolean;
}

function failure(error: unknown): VoiceCreationFailure {
  return {
    message:
      error instanceof VoiceApiError
        ? (error.detail ?? error.message)
        : error instanceof Error
          ? error.message
          : "Could not complete your request.",
    code: error instanceof VoiceApiError ? error.code : undefined,
    retryable: error instanceof VoiceApiError ? error.retryable : true,
  };
}

export const prepareVoiceProduct = createAsyncThunk<
  VoiceProductCreateResult,
  {
    files: File[];
    /** Recording, or null when the seller typed/pasted the description. */
    clip: VoiceClip | null;
    text?: string;
    context: VoiceProductCreateContext;
    clientRequestId: string;
  },
  { rejectValue: VoiceCreationFailure }
>("voiceProductCreate/prepare", async (input, api) => {
  try {
    return await analyzeProductCreation(
      input.files,
      input.clip,
      input.context,
      input.clientRequestId,
      api.signal,
      input.text,
    );
  } catch (error) {
    if (
      error instanceof VoiceApiError &&
      [
        VOICE_CREATION_CODES.FEATURE_DISABLED,
        VOICE_CREATION_CODES.SKILL_DISABLED,
      ].some((code) => code === error.code)
    ) {
      VoiceApiService.invalidateCapabilities();
    }
    return api.rejectWithValue(failure(error));
  }
});

export const loadSavedVoiceDrafts = createAsyncThunk<
  VoiceProductCreateResult[],
  void,
  { rejectValue: VoiceCreationFailure; state: RootState }
>(
  "voiceProductCreate/saved",
  async (_, api) => {
    try {
      return await getSavedVoiceDrafts();
    } catch (error) {
      return api.rejectWithValue(failure(error));
    }
  },
  {
    // One list request at a time (React StrictMode mounts effects twice in dev).
    condition: (_, { getState }) =>
      getState().voiceProductCreate.saved.status !== "loading",
  },
);
export const discardSavedVoiceDraft = createAsyncThunk<
  string,
  string,
  { rejectValue: VoiceCreationFailure }
>("voiceProductCreate/discardSaved", async (id, api) => {
  try {
    await discardVoiceCreation(id);
    return id;
  } catch (error) {
    return api.rejectWithValue(failure(error));
  }
});
export const resumeVoiceDraft = createAsyncThunk<
  VoiceProductCreateResult,
  string,
  { rejectValue: VoiceCreationFailure }
>("voiceProductCreate/resume", async (id, api) => {
  try {
    return await getVoiceCreationDraft(id);
  } catch (error) {
    return api.rejectWithValue(failure(error));
  }
});
export const answerVoiceQuestions = createAsyncThunk<
  VoiceProductCreateResult,
  { requestId: string; input: VoiceCreationAnswers; clip?: VoiceClip },
  { rejectValue: VoiceCreationFailure }
>("voiceProductCreate/answers", async (input, api) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 55000);
  const abort = () => controller.abort();
  api.signal.addEventListener("abort", abort);
  try {
    return await answerVoiceCreation(
      input.requestId,
      input.input,
      input.clip,
      controller.signal,
    );
  } catch (error) {
    return api.rejectWithValue(failure(error));
  } finally {
    clearTimeout(timeout);
    api.signal.removeEventListener("abort", abort);
  }
});

export const publishPreparedVoiceProduct = createAsyncThunk<
  ProductDetailsModal,
  {
    product: CreateProductModalState;
    locationOverride?: { latitude: number; longitude: number } | null;
    variantsConfig?: ListingTypeVariantConfig | null;
  },
  { state: RootState; rejectValue: VoiceCreationFailure }
>(
  "voiceProductCreate/publish",
  async (input, api) => {
    const meta = input.product.voiceCreation;
    if (!meta)
      return api.rejectWithValue({
        message: "There is no voice draft to publish.",
        disposition: "rejected",
      });
    let sent = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 55_000);
    const abort = () => controller.abort();
    api.signal.addEventListener("abort", abort);
    try {
      const pending = api.getState().voiceProductCreate.publication;
      let payload =
        pending.creationId === meta.creationId ? pending.payload : undefined;
      if (payload === undefined) {
        const location =
          input.locationOverride ??
          (await genericUtility.getCurrentUserLocation());
        if (
          api.signal.aborted ||
          controller.signal.aborted ||
          api.getState().voiceProductCreate.publication.requestId !==
            api.requestId
        )
          throw new Error("Publication stopped before sending.");
        payload = ProductConvertor.buildCreateProductPayload(
          input.product,
          location,
          input.variantsConfig,
        );
        api.dispatch(
          publicationPrepared({
            requestId: api.requestId,
            creationId: meta.creationId,
            voiceRequestId: meta.requestId,
            payload,
          }),
        );
      }
      if (
        api.getState().voiceProductCreate.publication.requestId !==
        api.requestId
      )
        throw new Error("This draft was closed.");
      sent = true;
      const product = await publishVoiceProduct(
        payload,
        meta.creationId,
        meta.requestId,
        controller.signal,
      );
      if (
        api.getState().voiceProductCreate.publication.requestId !==
        api.requestId
      ) {
        throw new Error("This draft was closed.");
      }
      return product;
    } catch (error) {
      const conflict =
        error instanceof VoiceApiError && error.httpStatus === 409;
      const rejected =
        !sent ||
        (error instanceof VoiceApiError &&
          error.httpStatus !== undefined &&
          error.httpStatus >= 400 &&
          error.httpStatus < 500 &&
          !conflict);
      return api.rejectWithValue({
        ...failure(error),
        cancelled:
          api.getState().voiceProductCreate.publication.requestId !==
          api.requestId,
        disposition: conflict
          ? "conflict"
          : rejected
            ? "rejected"
            : "uncertain",
      });
    } finally {
      clearTimeout(timeout);
      api.signal.removeEventListener("abort", abort);
    }
  },
  {
    condition: (_, { getState }) =>
      !["publishing", "conflict"].includes(
        getState().voiceProductCreate.publication.status,
      ),
  },
);

/** Abandon stops retrying locally; it cannot cancel a request the server already committed. */
export const abandonVoiceProduct = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  "voiceProductCreate/abandon",
  async (_, { getState }) => {
    const state = getState();
    // After an uncertain/conflicting publish the server may already have created the
    // product; reporting DISCARDED could close it and let cleanup remove its photos.
    if (state.voiceProductCreate.publication.status !== "idle") return;
    const requestId =
      state.createProduct.voiceCreation?.requestId ??
      state.voiceProductCreate.publication.voiceRequestId;
    if (requestId)
      VoiceApiService.reportOutcome(requestId, { outcome: "DISCARDED" });
  },
  {
    condition: (_, { getState }) =>
      getState().voiceProductCreate.publication.status !== "publishing",
  },
);
